import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AtomField from "../common/AtomField";
import "./Auth.css";
import Logo from "../shared/Logo";
import PasswordField from "../shared/PasswordField";
import DateOfBirthField from "../shared/DateOfBirthField";
import { checkDateOfBirth } from "../../utils/dateOfBirth";

const SEX_OPTIONS = ["Male","Female","Non-binary","Prefer not to say"];

const Register = () => {
  const { register } = useAuth();
  const navigate     = useNavigate();

  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [phone,    setPhone]    = useState("");
  const [dob,      setDob]      = useState({ day: "", month: "", year: "" });
  const [sex,      setSex]      = useState("");
  const [agreed,   setAgreed]   = useState(false);
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const errorRef = useRef(null);

  // On a phone the error sits at the top of a long form, far above the
  // button just pressed: bring it into view, or an under-age visitor sees
  // nothing happen. role="alert" announces it to screen readers as well.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setError("You must accept the Terms & Conditions and Privacy Policy to create an account.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    // The server checks this too and is the authority; checking here means
    // an under-age visitor is told before anything is sent.
    const birth = checkDateOfBirth(dob.day, dob.month, dob.year);
    if (birth.error) {
      setError(birth.error);
      return;
    }
    setError("");
    setLoading(true);
    try {
      await register(name, email, password, birth.iso, sex || null, phone || null);
      navigate(`/verify?email=${encodeURIComponent(email)}&type=email`);
    } catch (err) {
      setError(err.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page">
      <AtomField />

      <section className="auth-card auth-card-anim">
        <div className="auth-brand-icon">
          <Logo className="auth-logo" />
        </div>

        <h1 className="auth-anim-title">Create Account</h1>
        <p className="auth-anim-sub">Join Emporia Collections.</p>

        {error && (
          <div ref={errorRef} role="alert" className="auth-error auth-anim-field" style={{ "--i": 0 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <label className="form-field auth-anim-field" style={{ "--i": 0 }}>
            Name
            <input
              value={name} onChange={e => setName(e.target.value)}
              type="text" className="form-input" placeholder="Optional"
            />
          </label>
          <label className="form-field auth-anim-field" style={{ "--i": 1 }}>
            Email
            <input
              value={email} onChange={e => setEmail(e.target.value)}
              type="email" required className="form-input" autoComplete="email"
            />
          </label>
          <PasswordField
            label="Password"
            value={password} onChange={e => setPassword(e.target.value)}
            autoComplete="new-password" minLength={8}
            className="auth-anim-field" style={{ "--i": 2 }}
          />
          <label className="form-field auth-anim-field" style={{ "--i": 3 }}>
            Phone Number <span className="demo-opt">(optional)</span>
            <input
              value={phone} onChange={e => setPhone(e.target.value)}
              type="tel" autoComplete="tel" placeholder="e.g. 07XX XXX XXX" className="form-input"
            />
          </label>

          <DateOfBirthField
            value={dob}
            onChange={(v) => { setDob(v); if (error) setError(""); }}
            className="auth-anim-field" style={{ "--i": 4 }}
          />

          <label className="form-field auth-anim-field" style={{ "--i": 4 }}>
            Sex <span className="demo-opt">(optional)</span>
            <select value={sex} onChange={e => setSex(e.target.value)}
              className="form-input form-select">
              <option value="">— Select —</option>
              {SEX_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>

          <p className="demo-note auth-anim-field" style={{ "--i": 4 }}>
            We ask for your date of birth only to check you are old enough to
            have an account. It is stored securely and never shown publicly or
            to sellers. Neither it nor your sex is shared with third parties.
          </p>

          <label className="consent-label auth-anim-field" style={{ "--i": 5 }}>
            <input
              type="checkbox" className="consent-checkbox"
              checked={agreed}
              onChange={e => { setAgreed(e.target.checked); if (error) setError(""); }}
            />
            <span className="consent-text">
              I have read and agree to the{" "}
              <Link to="/terms" className="consent-link" target="_blank" rel="noreferrer">
                Terms &amp; Conditions
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="consent-link" target="_blank" rel="noreferrer">
                Privacy Policy
              </Link>
            </span>
          </label>

          <button
            className="form-button auth-anim-field" style={{ "--i": 6 }}
            type="submit" disabled={loading || !agreed}
          >
            {loading
              ? <span className="auth-btn-loading"><span/><span/><span/></span>
              : "Create Account"}
          </button>
        </form>

        <p className="auth-footer-link auth-anim-field" style={{ "--i": 7 }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </section>
    </main>
  );
};

export default Register;
